import { ReactElement, useCallback, useMemo, useState, memo } from 'react'
import { Box } from '@mui/material'
import TruncatedText from './TruncatedText'
import LocationView from './LocationView'
import { IMRTColumn } from '../Entity/interfaces'
import { isGeoFence, isGPS } from '../../utils/MiscellaneousUtils'

interface IGeoJSONTypeData {
    index: number
    dataValue: any
    item: IMRTColumn
    fontSize?: string
}

const GeoJSONTypeData = ({
    index,
    dataValue,
    item,
    fontSize,
}: IGeoJSONTypeData): ReactElement => {
    const [selectedGeoJSON, setSelectedGeoJSON] = useState<any>(null)
    const [isRowLocationDetailsMapOpen, setIsRowLocationDetailsMapOpen] =
        useState<boolean>(false)

    const locationString = useMemo(() => {
        const geoJsonValue = dataValue

        if (isGPS(item)) {
            return geoJsonValue?.lat && geoJsonValue?.long
                ? `lat: ${geoJsonValue.lat}, long: ${geoJsonValue.long}`
                : ''
        } else if (isGeoFence(item)) {
            return (
                geoJsonValue?.features?.find(
                    (t: any) => t.geometry.type === 'Point',
                )?.properties?.address ??
                geoJsonValue?.features?.find(
                    (t: any) => t.geometry.type === 'Polygon',
                )?.properties?.address ??
                ''
            )
        }
        return ''
    }, [dataValue, item])

    const isClickable = Boolean(locationString)

    const onClickLocationView = useCallback(
        (dataValue: any, item: IMRTColumn) => {
            if (isGPS(item)) {
                const lat = dataValue.lat
                const long = dataValue.long

                const geoJSONSample = {
                    type: 'FeatureCollection',
                    features: [],
                }
                const updatedGpsPoint = {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [long, lat],
                    },
                    properties: {
                        address: '',
                        radius: 0,
                        mapType: 'Circle',
                        zoom: 15,
                    },
                }
                const updatedGPSGeoJSON = {
                    ...geoJSONSample,
                    features: [updatedGpsPoint],
                }
                setSelectedGeoJSON(updatedGPSGeoJSON)
                setIsRowLocationDetailsMapOpen(true)
            } else {
                setSelectedGeoJSON(dataValue)
                setIsRowLocationDetailsMapOpen(true)
            }
        },
        [],
    )

    const onClickBackInRowLocationDetailsMap = useCallback(() => {
        setIsRowLocationDetailsMapOpen(false)
    }, [])

    const handleClick = useCallback(() => {
        if (isClickable) {
            onClickLocationView(dataValue, item)
        }
    }, [isClickable, onClickLocationView, dataValue, item])

    return (
        <>
            <Box
                id={`geo-json-location-${index}`}
                key={index}
                onClick={handleClick}
                sx={{
                    color: isClickable ? '#6d6dff' : '',
                    cursor: isClickable ? 'pointer' : '',
                    width: '100%',
                }}
            >
                <TruncatedText
                    id={`geo-json-text-${index}`}
                    sx={fontSize ? { fontSize } : undefined}
                    text={locationString}
                />
            </Box>

            {isRowLocationDetailsMapOpen && (
                <LocationView
                    isMapOpen={isRowLocationDetailsMapOpen}
                    onClickBackInMap={onClickBackInRowLocationDetailsMap}
                    geoJSON={selectedGeoJSON}
                />
            )}
        </>
    )
}

const MemoizedGeoJSONTypeData = memo(GeoJSONTypeData)

export default MemoizedGeoJSONTypeData
